import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid2';
import NewsPost from '../../../../types/communication/newsPost';

const LatestNews = (): JSX.Element => {

  const theme = useTheme();
  const [newsPosts,setNewsPosts] = useState<NewsPost[]>();

  useEffect(() => {
    getNewsPosts();
  }, []);

  const getNewsPosts = async () => {
    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/news/recent`)
      .then(result => result.json())
      .then(result => {
        let newsPosts: NewsPost[] = [];
        result.forEach((newsPost: NewsPost) => {
          newsPosts.push(prepareNewsPost(newsPost));
        });
        setNewsPosts(newsPosts);
      });

    }catch(error){
      console.error('Uncaught Error:', error);
    }
    
  }

  const BLOCK_TAGS = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'BLOCKQUOTE', 'TABLE'];

  const hasText = (node: Node) => (node.textContent ?? '').trim() !== '';

  // Teaser text for both news formats:
  //  - old news editor: <p>media</p><br>\nTeaser text<br>... -> the text up to the first <br>
  //  - Public Document Editor (CKEditor): <p>media</p><p>Teaser text</p>... -> the first <p> with text
  const getTeaserHtml = (doc: Document): string => {
    const body = doc.body;

    // Drop media along with any wrappers left without text, e.g. <p><a><img></a></p>
    body.querySelectorAll('img, iframe').forEach(media => {
      let target: Element = media;
      while (target.parentElement && target.parentElement !== body && !hasText(target.parentElement)) {
        target = target.parentElement;
      }
      target.remove();
    });

    const teaser = doc.createElement('div');
    for (const node of Array.from(body.childNodes)) {
      const tag = node.nodeType === Node.ELEMENT_NODE ? (node as Element).tagName : '';
      const started = teaser.hasChildNodes();

      if (tag === 'BR' || BLOCK_TAGS.includes(tag)) {
        if (started) {
          break;
        }
        if (tag !== 'BR' && hasText(node)) {
          return (node as Element).innerHTML.trim();
        }
        continue;
      }
      if (node.nodeType === Node.COMMENT_NODE || (!started && !hasText(node))) {
        continue;
      }
      teaser.appendChild(node.cloneNode(true));
    }
    return teaser.innerHTML.trim();
  }

  const prepareNewsPost = (newsPost: NewsPost) => {
    const doc = new DOMParser().parseFromString(newsPost.content ?? '', 'text/html');

    const image = doc.querySelector('img');
    newsPost.imageSrc = image?.getAttribute('src') ?? '';
    newsPost.imageLink = image?.closest('a')?.getAttribute('href') ?? '';
    newsPost.videoSrc = doc.querySelector('iframe')?.getAttribute('src') ?? '';
    newsPost.contentHtml = getTeaserHtml(doc);

    return newsPost;
  }

  return (
    <Box>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        marginBottom={4}
      >
        <Box>
          <Typography fontWeight={600} variant={'h5'} gutterBottom>
            Latest News
          </Typography>
        </Box>
        <Box display="flex" marginTop={{ xs: 2, md: 0 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            component={Link}
            href="/Publications/News"
          >
            View all
          </Button>
        </Box>
      </Box>
      <Grid container spacing={4}>
        {newsPosts?.map((item, i) => (
          <Grid size={12} key={i}>
            <Box
              component={Card}
              width={1}
              height={1}
              borderRadius={0}
              boxShadow={0}
              display={'flex'}
              flexDirection={{ xs: 'column', md: 'row' }}
              sx={{ backgroundImage: 'none', bgcolor: 'transparent' }}
            >
              <Box
                sx={{
                  width: { xs: 1, md: '50%' },
                }}
              >
                {
                  item.imageSrc &&
                  <Box
                    className = "is-clickable"
                    component={'img'}
                    loading="lazy"
                    height={1}
                    width={1}
                    onClick={() => item.imageLink && window.open(item.imageLink, '_blank')}
                    src={item.imageSrc}
                    alt={item.title}
                    sx={{
                      objectFit: 'cover',
                      maxHeight: 250,
                      borderRadius: 2,
                    }}
                  />
                }
                {
                  item.videoSrc &&
                  <Box
                    className = "is-clickable"
                    height={1}
                    width={1}
                    sx={{
                      objectFit: 'cover',
                      height: 250,
                      borderRadius: 2,
                    }}
                  >
                     <iframe 
                        height={"100%"}
                        width={"100%"}
                        title={item.title}
                        src={item.videoSrc} 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share">
                      </iframe>
                  </Box>
                }
               
              </Box>
              <CardContent
                className='is-clickable'
                //component={Link}
                //underline='none'
                onClick={() => {
                  window.location.href = `/Publications/News#news-${item.id}`;
                }}
                //href={`/Publications/News#news-${item.id}`}
                sx={{
                  width: { xs: 1, md: '50%' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  "&:hover": {
                    backgroundColor: theme.palette.alternate.main,
                    boxShadow: 2,
                  },
                }}
              >
                <Typography
                  fontWeight={700}
                  color="black"
                  marginTop={1}
                  component={Link}
                  href={`/Publications/News#news-${item.id}`}
                  underline='hover'
                >
                  {item.title}
                </Typography>
                <Box marginY={1}>
                  <Typography
                    fontWeight={600}
                    variant={'body2'}
                    color={'primary'}
                    component={'i'}
                  >
                    {item.dateDisplayDay} {item.dateDisplayMonth} {item.dateDisplayYear}
                  </Typography>
                </Box>
                <Typography color="text.secondary">
                  <span dangerouslySetInnerHTML={{ __html: item.contentHtml }}></span>
                </Typography>
              </CardContent>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default LatestNews;
